import { useState } from 'react';
import { useClienteResumen } from '../api/queries';
import { useProveedorResumen } from '../api/queries-finanzas';
import { Modal } from './Modal';
import { StatusBadge } from './Badges';
import { TabBar } from './FinanceBits';
import { formatDate, formatMoney, formatRate } from '../lib/format';

function Cargando({ texto }: { texto: string }) {
  return <p className="muted">{texto}</p>;
}

export function ResumenClienteModal({
  clienteId,
  onClose,
}: {
  clienteId: string;
  onClose: () => void;
}) {
  const { data, isLoading, isError, refetch } = useClienteResumen(clienteId);
  const [tab, setTab] = useState<'facturas' | 'cobranzas' | 'notas' | 'asientos'>('facturas');

  return (
    <Modal
      title={data?.cliente.nombre ?? 'Resumen del cliente'}
      eyebrow="Cliente · CxC y movimientos"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn--outline" onClick={onClose}>
            Cerrar
          </button>
          {isError ? (
            <button type="button" className="btn btn--primary" onClick={() => void refetch()}>
              Reintentar
            </button>
          ) : null}
        </>
      }
    >
      <div className="modal-card__body">
        {isLoading ? <Cargando texto="Cargando ventas, cobros y asientos…" /> : null}
        {isError ? <p className="inline-note">No se pudo cargar el resumen del cliente.</p> : null}
        {data ? (
          <>
            <div className="detail-grid">
              <div className="detail-block">
                <span>RIF</span>
                <strong>{data.cliente.rif}</strong>
                <small>{data.cliente.vendedorNombre ?? 'Sin vendedor asignado'}</small>
              </div>
              <div className="detail-block">
                <span>Contacto</span>
                <strong>{data.cliente.telefono ?? 'Sin teléfono'}</strong>
                <small>{data.cliente.email ?? data.cliente.direccion ?? 'Sin datos'}</small>
              </div>
              <div className="detail-block">
                <span>Condición</span>
                <strong>{data.cliente.diasCredito} días</strong>
                <small>Límite {formatMoney(data.cliente.limiteCredito, 'USD')}</small>
              </div>
              <div className="detail-block">
                <span>Saldo a favor</span>
                <strong>{formatMoney(data.cliente.saldoFavorUsd ?? 0, 'USD')}</strong>
                <small>{data.cliente.activo ? 'Cliente activo' : 'Cliente inactivo'}</small>
              </div>
            </div>

            <div className="summary-strip summary-strip--4">
              <div className="summary-tile summary-tile--warning">
                <span>CxC pendiente</span>
                <strong>{formatMoney(data.totales.cxcVes, 'VES')}</strong>
                <small>{data.facturas.filter((f) => f.saldoUsd > 0).length} facturas abiertas</small>
              </div>
              <div className="summary-tile summary-tile--danger">
                <span>CxC vencida</span>
                <strong>{formatMoney(data.totales.cxcVencidaVes, 'VES')}</strong>
                <small>Vencimiento anterior a hoy</small>
              </div>
              <div className="summary-tile summary-tile--accent">
                <span>Facturado</span>
                <strong>{formatMoney(data.totales.facturadoVes, 'VES')}</strong>
                <small>Facturas no anuladas</small>
              </div>
              <div className="summary-tile">
                <span>Cobrado</span>
                <strong>{formatMoney(data.totales.cobradoVes, 'VES')}</strong>
                <small>{data.cobranzas.length} recibos</small>
              </div>
            </div>

            <TabBar
              label="Detalle del cliente"
              value={tab}
              onChange={setTab}
              tabs={[
                { value: 'facturas', label: `Facturas (${data.facturas.length})` },
                { value: 'cobranzas', label: `Cobranzas (${data.cobranzas.length})` },
                { value: 'notas', label: `Notas (${data.notas.length})` },
                { value: 'asientos', label: `Asientos (${data.asientos.length})` },
              ]}
            />

            {tab === 'facturas' ? (
              <>
<h3 className="card-title">Ventas y facturas</h3>
            <div className="table-scroll">
              <table className="data-table data-table--compact">
                <thead>
                  <tr><th>Factura</th><th>Fecha</th><th>Vence</th><th>Total</th><th>Saldo</th><th>Estado</th></tr>
                </thead>
                <tbody>
                  {data.facturas.length === 0 ? <tr><td colSpan={6}><div className="table-empty">Sin facturas.</div></td></tr> : null}
                  {data.facturas.map((f) => (
                    <tr key={f.id}>
                      <td><strong>{f.numero}</strong><br /><small>{f.tipo}</small></td>
                      <td>{formatDate(f.fecha)}</td>
                      <td>{f.vencimiento ? formatDate(f.vencimiento) : '—'}</td>
                      <td className="align-right">{formatMoney(f.totalVes, 'VES')}</td>
                      <td className="align-right">{formatMoney(f.saldoVes, 'VES')}</td>
                      <td><StatusBadge estado={f.estado} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              </>
            ) : null}
            {tab === 'cobranzas' ? (
              <>
<h3 className="card-title">Cobranzas</h3>
            <div className="table-scroll">
              <table className="data-table data-table--compact">
                <thead>
                  <tr><th>Recibo</th><th>Fecha</th><th>Método</th><th>Monto</th><th>Estado</th></tr>
                </thead>
                <tbody>
                  {data.cobranzas.length === 0 ? <tr><td colSpan={5}><div className="table-empty">Sin cobranzas.</div></td></tr> : null}
                  {data.cobranzas.map((c) => (
                    <tr key={c.id}>
                      <td>{c.numero ?? '—'}<br /><small>{c.referencia ?? 'Sin referencia'}</small></td>
                      <td>{formatDate(c.fecha)}</td>
                      <td>{c.metodo}</td>
                      <td className="align-right">{formatMoney(c.montoVes, 'VES')}</td>
                      <td><StatusBadge estado={c.estado} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              </>
            ) : null}
            {tab === 'notas' ? (
              <>
<h3 className="card-title">Notas de crédito / débito</h3>
            <div className="table-scroll">
              <table className="data-table data-table--compact">
                <thead>
                  <tr><th>Tipo</th><th>Número</th><th>Fecha</th><th>Total</th><th>Saldo a favor</th><th>Estado</th></tr>
                </thead>
                <tbody>
                  {data.notas.length === 0 ? <tr><td colSpan={6}><div className="table-empty">Sin notas.</div></td></tr> : null}
                  {data.notas.map((n) => (
                    <tr key={n.id}>
                      <td>{n.tipo === 'CREDITO' ? 'Crédito' : 'Débito'}</td>
                      <td>{n.numero}</td>
                      <td>{formatDate(n.fecha)}</td>
                      <td className="align-right">{formatMoney(n.totalVes, 'VES')}</td>
                      <td className="align-right">{formatMoney(n.saldoFavorUsd, 'USD')}</td>
                      <td><StatusBadge estado={n.estado} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              </>
            ) : null}
            {tab === 'asientos' ? (
              <>
<h3 className="card-title">Asientos contables</h3>
            <div className="table-scroll">
              <table className="data-table data-table--compact">
                <thead>
                  <tr><th>#</th><th>Fecha</th><th>Descripción</th><th>Origen</th><th className="align-right">Debe</th><th className="align-right">Haber</th></tr>
                </thead>
                <tbody>
                  {data.asientos.length === 0 ? <tr><td colSpan={6}><div className="table-empty">Sin asientos vinculados.</div></td></tr> : null}
                  {data.asientos.map((a) => (
                    <tr key={a.id}>
                      <td>{a.numero}</td>
                      <td>{formatDate(a.fecha)}</td>
                      <td>{a.descripcion}</td>
                      <td>{a.origen}</td>
                      <td className="align-right">{formatMoney(a.debeUsd, 'USD')}</td>
                      <td className="align-right">{formatMoney(a.haberUsd, 'USD')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              </>
            ) : null}
            <p className="page-footnote">
              Tasa de referencia del cliente: {formatRate(data.facturas[0]?.tasa ?? 1)} VES/USD.
            </p>
          </>
        ) : null}
      </div>
    </Modal>
  );
}

export function ResumenProveedorModal({
  proveedorId,
  onClose,
}: {
  proveedorId: string;
  onClose: () => void;
}) {
  const { data, isLoading, isError, refetch } = useProveedorResumen(proveedorId);
  const [tab, setTab] = useState<'compras' | 'pagos' | 'asientos'>('compras');

  return (
    <Modal
      title={data?.proveedor.nombre ?? 'Resumen del proveedor'}
      eyebrow="Proveedor · CxP y movimientos"
      onClose={onClose}
      wide
      footer={
        <>
          <button type="button" className="btn btn--outline" onClick={onClose}>
            Cerrar
          </button>
          {isError ? (
            <button type="button" className="btn btn--primary" onClick={() => void refetch()}>
              Reintentar
            </button>
          ) : null}
        </>
      }
    >
      <div className="modal-card__body">
        {isLoading ? <Cargando texto="Cargando compras, pagos y asientos…" /> : null}
        {isError ? <p className="inline-note">No se pudo cargar el resumen del proveedor.</p> : null}
        {data ? (
          <>
            <div className="detail-grid">
              <div className="detail-block">
                <span>RIF</span>
                <strong>{data.proveedor.rif}</strong>
                <small>{data.proveedor.activo ? 'Activo' : 'Inactivo'}</small>
              </div>
              <div className="detail-block">
                <span>Contacto</span>
                <strong>{data.proveedor.telefono ?? 'Sin teléfono'}</strong>
                <small>{data.proveedor.email ?? data.proveedor.direccion ?? 'Sin datos'}</small>
              </div>
              <div className="detail-block">
                <span>Condición</span>
                <strong>{data.proveedor.diasCredito} días</strong>
                <small>Límite {formatMoney(data.proveedor.limiteCreditoUsd, 'USD')}</small>
              </div>
              <div className="detail-block">
                <span>CxP</span>
                <strong>{formatMoney(data.totales.cxpVes, 'VES')}</strong>
                <small>Vencida {formatMoney(data.totales.cxpVencidaVes, 'VES')}</small>
              </div>
            </div>

            <div className="summary-strip summary-strip--4">
              <div className="summary-tile summary-tile--warning">
                <span>CxP pendiente</span>
                <strong>{formatMoney(data.totales.cxpVes, 'VES')}</strong>
                <small>{data.compras.filter((c) => c.saldoUsd > 0).length} compras abiertas</small>
              </div>
              <div className="summary-tile summary-tile--danger">
                <span>CxP vencida</span>
                <strong>{formatMoney(data.totales.cxpVencidaVes, 'VES')}</strong>
                <small>Vencimiento anterior a hoy</small>
              </div>
              <div className="summary-tile summary-tile--accent">
                <span>Comprado</span>
                <strong>{formatMoney(data.totales.compradoVes, 'VES')}</strong>
                <small>Compras no anuladas</small>
              </div>
              <div className="summary-tile">
                <span>Pagado</span>
                <strong>{formatMoney(data.totales.pagadoVes, 'VES')}</strong>
                <small>{data.pagos.length} pagos</small>
              </div>
            </div>

            <TabBar
              label="Detalle del proveedor"
              value={tab}
              onChange={setTab}
              tabs={[
                { value: 'compras', label: `Compras (${data.compras.length})` },
                { value: 'pagos', label: `Pagos (${data.pagos.length})` },
                { value: 'asientos', label: `Asientos (${data.asientos.length})` },
              ]}
            />

            {tab === 'compras' ? (
              <>
<h3 className="card-title">Compras</h3>
            <div className="table-scroll">
              <table className="data-table data-table--compact">
                <thead>
                  <tr><th>Documento</th><th>Fecha</th><th>Vence</th><th>Total</th><th>Saldo</th><th>Estado</th></tr>
                </thead>
                <tbody>
                  {data.compras.length === 0 ? <tr><td colSpan={6}><div className="table-empty">Sin compras.</div></td></tr> : null}
                  {data.compras.map((c) => (
                    <tr key={c.id}>
                      <td><strong>{c.numeroInterno}</strong><br /><small>{c.numeroFactura}</small></td>
                      <td>{formatDate(c.fecha)}</td>
                      <td>{c.vencimiento ? formatDate(c.vencimiento) : '—'}</td>
                      <td className="align-right">{formatMoney(c.totalVes, 'VES')}</td>
                      <td className="align-right">{formatMoney(c.saldoVes, 'VES')}</td>
                      <td><StatusBadge estado={c.estado} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              </>
            ) : null}
            {tab === 'pagos' ? (
              <>
<h3 className="card-title">Pagos</h3>
            <div className="table-scroll">
              <table className="data-table data-table--compact">
                <thead>
                  <tr><th>Pago</th><th>Fecha</th><th>Moneda</th><th>Monto</th><th>Estado</th></tr>
                </thead>
                <tbody>
                  {data.pagos.length === 0 ? <tr><td colSpan={5}><div className="table-empty">Sin pagos.</div></td></tr> : null}
                  {data.pagos.map((p) => (
                    <tr key={p.id}>
                      <td>{p.numero}<br /><small>{p.referencia ?? 'Sin referencia'}</small></td>
                      <td>{formatDate(p.fecha)}</td>
                      <td>{p.moneda}</td>
                      <td className="align-right">{formatMoney(p.montoVes, 'VES')}</td>
                      <td><StatusBadge estado={p.estado === 'REGISTRADO' ? 'pagada' : 'anulada'} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              </>
            ) : null}
            {tab === 'asientos' ? (
              <>
<h3 className="card-title">Asientos contables</h3>
            <div className="table-scroll">
              <table className="data-table data-table--compact">
                <thead>
                  <tr><th>#</th><th>Fecha</th><th>Descripción</th><th>Origen</th><th className="align-right">Debe</th><th className="align-right">Haber</th></tr>
                </thead>
                <tbody>
                  {data.asientos.length === 0 ? <tr><td colSpan={6}><div className="table-empty">Sin asientos vinculados.</div></td></tr> : null}
                  {data.asientos.map((a) => (
                    <tr key={a.id}>
                      <td>{a.numero}</td>
                      <td>{formatDate(a.fecha)}</td>
                      <td>{a.descripcion}</td>
                      <td>{a.origen}</td>
                      <td className="align-right">{formatMoney(a.debeUsd, 'USD')}</td>
                      <td className="align-right">{formatMoney(a.haberUsd, 'USD')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              </>
            ) : null}
          </>
        ) : null}
      </div>
    </Modal>
  );
}
